<script lang="ts">
	/**
	 * The inline purchase flow (§14) — what used to be a confirmation popup, as steps on the page.
	 *
	 * A dialog was the wrong shape for this. It arrived *after* the decision, over a dimmed copy of
	 * the thing being decided, and it had to hold a sign-in, a billing question, the terms and the
	 * options in a window sized for a receipt. Now the page becomes the checkout and the steps are
	 * the page: **who is buying, where they are buying from, and what they are agreeing to**, in
	 * the order those questions can actually be answered.
	 *
	 * That order is not cosmetic. The email comes first because until the buyer is known the store
	 * cannot say what else it needs — §10.1's country question is about *that customer*, and the
	 * answer to "do we already hold one" is unavailable until they sign in. Terms come last
	 * because they are the thing being agreed to and the pay button sits under them.
	 *
	 * **One step is open at a time**, and a finished one collapses to the answer it captured with
	 * an Edit beside it. A completed step that stays open is a form the customer has to scroll past
	 * on the way to the one they are being asked to fill in; collapsed, the answer is still on
	 * screen and still correctable, which is the property that matters.
	 *
	 * Nothing here commits anything. It raises `pay`, and the caller decides what that means —
	 * a checkout session for a purchase, `changeSubscriptionPlan` for a tier change.
	 */

	import { Button, Checkbox, Input, Label } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import CountrySelect from './CountrySelect.svelte';
	import FormField from './FormField.svelte';
	import PriceDisplay from './PriceDisplay.svelte';
	import SignInPanel from './SignInPanel.svelte';
	import PurchaseAccount from './PurchaseAccount.svelte';
	import { countryName } from '../Countries';
	import { track } from '../stores/analytics';
	import { displayNameOf } from '../Utilities';
	import AbstractID from '../AbstractID';
	import type { Clickwrap, ProductOption, StorefrontCurrency } from '../AppTypes';

	/** What is being committed to. A change asks neither for an email nor for a billing identity. */
	export let mode: 'buy' | 'change' | 'seats' = 'buy';
	/**
	 * A change that keeps the tier and moves only the billing period.
	 *
	 * Passed in rather than derived: only the page knows, since it takes comparing the target price
	 * against the one held, and `OrderSummary` takes the same flag for the same reason.
	 */
	export let isBillingChange: boolean = false;
	export let isSignedIn: boolean = false;

	/** §10.1, bindable. Empty means unanswered — the caller sends nothing rather than a blank. */
	export let billingCountry: string = '';
	export let billingCompany: string = '';
	/**
	 * The professional-individual declaration (§10.1), bindable. Offered only where the chosen
	 * country takes professionals as businesses and no company has been named — a company is
	 * already a business, and elsewhere the box would change nothing it claims to.
	 */
	export let billingProfessional: boolean = false;
	export let professionalCountries: string[] = [];
	$: professionalOffered =
		billingCountry.length > 0 &&
		professionalCountries.includes(billingCountry.toUpperCase()) &&
		billingCompany.trim().length === 0;
	/**
	 * Whether the country is **required**, as against merely asked.
	 *
	 * The step is always shown on a purchase, because a company name is worth having on every
	 * store — it is the name on the invoice (§4.6) and it picks the business tax rate (§10.1) — and
	 * a customer who has bought as a business before and sees the box empty reads that as the store
	 * having forgotten. But only a store that computes its own tax cannot proceed without the
	 * country, so only there does an empty one hold the purchase up.
	 */
	export let countryRequired: boolean = false;
	/** The server's own sentence about a country it could not price, put back on the field. */
	export let countryError: string = '';

	/** The terms this product requires (§4.7), and what has been ticked and typed. */
	export let terms: Clickwrap[] = [];
	export let ticked: Record<string, boolean> = {};
	export let answers: Record<string, string> = {};
	/** The options offered (§4.8), and which are ticked. Never gates anything. */
	export let options: ProductOption[] = [];
	export let optionTicked: Record<string, boolean> = {};
	/** Whether one option cannot be taken right now, and why — the caller owns both rules. */
	export let optionDisabled: (option: ProductOption) => boolean = () => false;
	export let optionNote: (option: ProductOption) => string = () => '';

	export let currency: StorefrontCurrency | undefined = undefined;
	export let currencyCode: string = '';
	/** Whether the store is taking purchases at all. */
	export let isOpen: boolean = true;
	/** True while the purchase is being committed, so nothing can be double-submitted. */
	export let busy: boolean = false;
	/**
	 * Whether the caller is ready to be paid — a change waits on its quote, a purchase does not.
	 * The terms are gated here, because this component is what renders them.
	 */
	export let canCommit: boolean = true;
	/** What the pay button says. Only the caller knows whether this charges or schedules. */
	export let commitLabel: string = 'Pay';

	const dispatcher = createEventDispatcher();

	/**
	 * The account the purchase will land on, read where `PurchaseAccount` reads it.
	 *
	 * Derived rather than taken as a prop, so the collapsed line and the open step cannot disagree
	 * about who is buying — which on a shared machine is the difference between a purchase on the
	 * right account and one discovered by the entitlement never arriving.
	 */
	$: tokenData = isSignedIn ? AbstractID.tokenData : null;
	$: accountEmail = tokenData?.email ?? '';
	$: accountName = displayNameOf(tokenData) === accountEmail ? '' : displayNameOf(tokenData);

	/**
	 * The question a term asks, or null.
	 *
	 * `?? null` and a length test rather than a truthiness check, because a server that predates
	 * the column sends `undefined` — which is not null to anything comparing against it, and would
	 * draw an unlabelled required text box under every term in the store.
	 */
	function questionOf(term: Clickwrap): string | null {
		const label = (term.inputLabel ?? '').trim();
		return label.length === 0 ? null : label;
	}

	/** Whether this option adds money rather than taking it off — the sign, in one place. */
	function isCharging(option: ProductOption): boolean {
		return option.adjustmentType === 'percent'
			? (option.percentAdjustment ?? 0) > 0
			: option.adjustmentType === 'fixed' && (option.amount ?? 0) > 0;
	}

	$: isChange = mode !== 'buy';
	/** A change acts on a subscription that already exists, so its buyer is known by construction. */
	$: needsAccount = !isChange;
	/**
	 * Whether this commit owes the terms an answer.
	 *
	 * Two changes re-ask nothing, and it is the same reason twice: **the customer is not buying a
	 * different product.** A seat change moves the count on the tier they already hold, and a
	 * billing change moves between two prices of it — monthly to yearly, the same tier either way.
	 * Terms are scoped to the product, so in both cases the agreement on offer is one they are
	 * already under, and re-asking makes somebody re-read and re-tick it in order to change how
	 * often they are billed. A move to a *different* tier does owe them, because that tier may
	 * carry terms of its own.
	 */
	$: needsTerms = mode !== 'seats' && !isBillingChange && terms.length > 0;
	$: needsOptions = mode === 'buy' && options.length > 0;

	$: accountDone = !needsAccount || isSignedIn;

	/**
	 * The billing step is finished when the customer **says** it is, not when it has enough to
	 * proceed.
	 *
	 * The distinction matters because the company is optional. Treating "nothing is blocking" as
	 * "done" would collapse the step the instant it opened on every store that does not require a
	 * country — which is most of them — and the customer would never get to give a company name at
	 * all. So it takes an explicit Continue, and the only thing that gates that button is a country
	 * where one is actually required.
	 */
	let billingContinued: boolean = false;
	$: billingReady = !countryRequired || billingCountry.length > 0;
	$: billingDone = !needsAccount || (billingContinued && billingReady);
	$: allTermsTicked = terms.every(term => ticked[term.uuid] === true);
	$: allQuestionsAnswered = terms.every(
		term => questionOf(term) === null || (answers[term.uuid] ?? '').trim().length > 0
	);
	$: termsDone = !needsTerms || (allTermsTicked && allQuestionsAnswered);

	/*
	 * The step the customer is looking at, recorded once per step rather than on every render.
	 *
	 * `openStep` is derived, so it re-evaluates constantly; without the guard this would emit an
	 * event on every keystroke in the email field. The guard is a plain variable rather than a
	 * reactive one for the same reason — a reactive flag written inside the block it guards is a
	 * loop.
	 */
	let reportedStep: string = '';
	$: if (openStep !== reportedStep) {
		reportedStep = openStep;
		track('checkout_step', { properties: { step: openStep, action: 'opened', mode } });
	}

	/**
	 * Which step is open, derived rather than stored.
	 *
	 * Derived because the answer is "the first one that is not finished", and a stored index would
	 * be a second copy of that fact — one that goes stale the moment a customer edits an earlier
	 * step, which is exactly when it matters. `editing` is the one thing that overrides it, and it
	 * is cleared as soon as the step it names is finished again.
	 */
	let editing: '' | 'account' | 'billing' = '';
	$: openStep =
		editing === 'account' && needsAccount
			? 'account'
			: editing === 'billing' && needsAccount
				? 'billing'
				: !accountDone
					? 'account'
					: !billingDone
						? 'billing'
						: 'terms';

	/** Numbered as they are shown, so a store that asks for no billing has no gap at 2. */
	$: stepNumbers = (() => {
		let next = 1;
		return {
			account: needsAccount ? next++ : 0,
			billing: needsAccount ? next++ : 0,
			terms: next
		};
	})();

	function fnEdit(step: 'account' | 'billing'): void {
		editing = step;
	}

	function fnCloseEdit(): void {
		editing = '';
	}

	/**
	 * One step's number chip, in the three states a step can be in.
	 *
	 * A whole class string per branch rather than an interpolated fragment: Tailwind's scanner
	 * reads source text, so a class assembled at runtime is invisible to it and the rule silently
	 * never ships (the same reason `rungColor` returns a hex for an inline style).
	 *
	 * Filled in **primary**, not green. Green is the money colour throughout this app — a
	 * discount, an amount saved — and a green tick beside "Billing details" reads as something
	 * having been earned rather than answered.
	 */
	function chipClass(done: boolean, current: boolean): string {
		const base =
			'shrink-0 w-6 h-6 rounded-full text-xs font-semibold flex items-center justify-center';
		if (done) return `${base} bg-primary-500 text-white`;
		if (current) return `${base} bg-primary-500 text-white`;
		return `${base} bg-gray-200 text-gray-500 dark:bg-zinc-700 dark:text-zinc-400`;
	}

	/** The collapsed billing line: what was answered, or that nothing was. */
	$: billingSummary = [
		billingCountry.length > 0 ? countryName(billingCountry) : 'No country given',
		billingCompany.trim(),
		professionalOffered && billingProfessional ? 'Buying as a professional' : ''
	]
		.filter(part => part.length > 0)
		.join(' · ');

	/**
	 * What the last step is called, which is what it actually contains.
	 *
	 * Not "Confirm and pay": that is what the **button** says, and a heading repeating its own
	 * button names the action twice and the content nought times.
	 */
	$: lastStepTitle = needsTerms && needsOptions
		? 'Terms and extras'
		: needsTerms
			? 'Terms'
			: needsOptions
				? 'Optional extras'
				: 'Payment';

	/** The explicit end of the billing step — see `billingContinued`. */
	function fnContinueBilling(): void {
		if (!billingReady) return;
		billingContinued = true;
		editing = '';
		// §12.2. A completed step, named, so the funnel can say which one a customer stopped at
		// rather than only that they stopped somewhere inside the flow.
		track('checkout_step', { properties: { step: 'billing', action: 'completed', mode } });
	}

	/**
	 * Signing in is a **step**, not the purchase.
	 *
	 * This is the one thing the popup did that the inline flow deliberately does not: there,
	 * signing in *was* the confirmation, because the window had no room to carry on afterwards and
	 * asking for a second press re-confirmed a decision made two screens earlier. Here the customer
	 * stays exactly where they are, with the next question already on screen — so signing in
	 * advances, and the purchase is still committed by the button under the terms.
	 */
	function fnSignedIn(): void {
		editing = '';
		track('checkout_step', { properties: { step: 'account', action: 'completed', mode } });
		dispatcher('signedin');
	}
</script>

<!--
	One surface, three parts — not three floating cards.

	A checkout is a single form asked in an order, and the design system already says how to draw
	one of those: a `widget` with `border-widget-color` hairlines between its sections. Three
	separate widgets read as three unrelated things and lose the one fact the layout should carry,
	which is that these are **steps**. The numbered spine down the left is the same fact stated
	again — legitimate here precisely because the content really is a sequence: the email has to be
	answered before the store knows what else to ask.
-->
<div class="widget bg-depth shadow-widget divide-y divide-gray-100 dark:divide-zinc-700">
	{#if needsAccount}
		<!--
			Step 1 — who is buying. First because until the buyer is known the store cannot say
			what else it needs: §10.1's question is about *that customer*.
		-->
		<section class="py-4 first:pt-0 last:pb-0" aria-labelledby="step-account">
			<div class="flex items-center gap-3">
				<span class={chipClass(accountDone, openStep === 'account')}>
					{#if accountDone}
						<i class="fa-solid fa-check text-[10px]" />
					{:else}
						{stepNumbers.account}
					{/if}
				</span>
				<h3
					id="step-account"
					class="font-semibold grow min-w-0 {openStep === 'account' ? '' : 'text-default'}"
				>
					Your email
				</h3>
				{#if accountDone && openStep !== 'account'}
					<!--
						The answer and its Change on the header row, not stranded under it: a
						collapsed step has to stay readable and correctable in one glance, and a
						control on its own line reads as a second step rather than as this one's.
					-->
					<span class="text-sm text-default truncate">{accountEmail}</span>
					<button
						type="button"
						class="text-sm text-primary-500 hover:underline shrink-0"
						on:click={() => fnEdit('account')}
					>
						Change
					</button>
				{/if}
			</div>

			{#if openStep === 'account'}
				<div class="mt-4 pl-9">
					{#if isSignedIn}
						<!--
							Already signed in: the account is stated and the step is done, through the
							one component that says which account a purchase lands on. The email
							leads because it identifies the account and is where the receipt goes —
							an account created from an address alone has no name.
						-->
						<PurchaseAccount label="Purchasing as" />
						<p class="text-xs text-default mt-1">
							Your purchase and its receipt will be attached to this account.
						</p>
						{#if editing === 'account'}
							<Button size="xs" color="alternative" class="mt-3" on:click={fnCloseEdit}>Done</Button>
						{/if}
					{:else}
						<!--
							`SignInPanel` renders inline and owns no window — the caller supplies the
							surface, which is now the page rather than a dialog inside a dialog. It
							never redirects on any branch, so the seat count, the coupon and
							everything else chosen on this page survives being signed in.
						-->
						<SignInPanel on:signedin={fnSignedIn} />
					{/if}
				</div>
			{/if}
		</section>

		<!--
			Step 2 — the billing identity (§10.1).

			Always asked on a purchase, and prefilled from what the store already holds. The company
			is the half that earns it everywhere: it is the name on the invoice (§4.6) and it picks
			which of a jurisdiction's two rates applies, and it is optional, so it never suppresses
			the question the way a known country suppresses the country.
		-->
		<section class="py-4 first:pt-0 last:pb-0" aria-labelledby="step-billing">
			<div class="flex items-center gap-3">
				<span class={chipClass(billingDone && openStep !== 'billing', openStep === 'billing')}>
					{#if billingDone && openStep !== 'billing'}
						<i class="fa-solid fa-check text-[10px]" />
					{:else}
						{stepNumbers.billing}
					{/if}
				</span>
				<h3
					id="step-billing"
					class="font-semibold grow min-w-0 {openStep === 'billing' ? '' : 'text-default'}"
				>
					Billing details
				</h3>
				{#if billingDone && openStep !== 'billing'}
					<span class="text-sm text-default truncate">{billingSummary}</span>
					<button
						type="button"
						class="text-sm text-primary-500 hover:underline shrink-0"
						on:click={() => fnEdit('billing')}
					>
						Change
					</button>
				{/if}
			</div>

			{#if openStep === 'billing'}
				<div class="mt-4 pl-9 space-y-3">
					<p class="text-sm text-default">
						{#if countryRequired}
							Your country decides the tax on this purchase. You'll enter the rest of your
							billing address on the payment page.
						{:else}
							Buying for a company? Give its name and country and we'll put them on the
							invoice. You'll enter the rest of your billing address on the payment page.
						{/if}
					</p>
					<div class="grid sm:grid-cols-2 gap-4">
						<FormField
							id="checkout-country"
							label="Country"
							required={countryRequired}
							error={countryError}
						>
							<!--
								`CountrySelect`, so what the customer types can never become the value:
								a text field accepts "UK", which is not a country code, and the tax
								table then refuses the sale naming a country nobody chose.
							-->
							<CountrySelect
								id="checkout-country"
								bind:value={billingCountry}
								required={countryRequired}
							/>
						</FormField>
						<FormField
							id="checkout-company"
							label="Company"
							description="Leave blank if this is a personal purchase."
						>
							<Input id="checkout-company" bind:value={billingCompany} maxlength={240} />
						</FormField>
					</div>
					{#if professionalOffered}
						<label class="flex items-start gap-2 text-sm">
							<Checkbox spacing="" class="shrink-0 mt-0.5" bind:checked={billingProfessional} />
							<span>
								I'm buying for my work as a sole trader or freelancer, without a company.
								<span class="block text-xs text-default">Taxed as a business purchase in {countryName(billingCountry)}.</span>
							</span>
						</label>
					{/if}
					<Button size="sm" color="primary" disabled={!billingReady} on:click={fnContinueBilling}>
						Continue
					</Button>
				</div>
			{/if}
		</section>
	{/if}

	<!--
		The last step — what is being agreed to, and the button that commits it.

		The terms gate the purchase (§4.7) and the options never do (§4.8): a buyer who ticks none
		of the options buys at list price, which is the ordinary purchase and has to stay so.
	-->
	<section class="py-4 first:pt-0 last:pb-0" aria-labelledby="step-terms">
		<div class="flex items-center gap-3">
			<span class={chipClass(false, openStep === 'terms')}>{stepNumbers.terms}</span>
			<h3
				id="step-terms"
				class="font-semibold grow min-w-0 {openStep === 'terms' ? '' : 'text-default'}"
			>
				{lastStepTitle}
			</h3>
		</div>

		{#if openStep === 'terms'}
			<div class="mt-4 pl-9 space-y-5">
				{#if needsTerms}
					<div class="space-y-3">
						{#each terms as term (term.uuid)}
							<div class="space-y-1">
								<!--
									`spacing=""` drops flowbite's own `me-2` so the gap is this row's
									`gap-2` alone, and `mt-0.5` drops the 16px box by the 2px of
									half-leading a 14px/20px line carries — without it `items-start`
									tops the box against the line box rather than the glyphs.
								-->
								<label class="flex items-start gap-2 text-sm font-medium">
									<Checkbox
										bind:checked={ticked[term.uuid]}
										disabled={!isOpen || busy}
										spacing=""
										class="mt-0.5 shrink-0" />
									<span>{term.title}</span>
								</label>
								<!-- `.richtext` because this is HTML the app did not author: without it
								     preflight collapses paragraphs and strips bullets, and a term
								     nobody can read is a term nobody agreed to. -->
								<div class="richtext pl-6 text-xs text-default">{@html term.bodyHTML}</div>
								{#if questionOf(term) !== null}
									<!--
										Required, and gated in `termsDone` rather than by a native
										`required`: the browser's own bubble talks about a text box
										and would replace the screen's own sentence.
									-->
									<div class="pl-6 pt-1">
										<Label for={`term-input-${term.uuid}`} class="mb-1 text-xs font-normal">
											{questionOf(term)}
										</Label>
										<Input
											id={`term-input-${term.uuid}`}
											size="sm"
											maxlength={500}
											bind:value={answers[term.uuid]}
											disabled={!isOpen || busy}
											aria-required="true" />
									</div>
								{/if}
							</div>
						{/each}
					</div>
				{/if}

				{#if needsOptions}
					<div class="space-y-3">
						<p class="card-label"><i class="fa-solid fa-sliders text-[11px]" />Optional extras</p>
						{#each options as option (option.uuid)}
							<!--
								A ticked option is a commitment the customer is making in exchange for
								money off, so the ticked state is drawn rather than left to the
								checkbox alone: it is the one choice on this screen they are opting
								*into*.
							-->
							<div
								class="rounded-xl border p-3 transition-colors"
								class:border-primary-500={optionTicked[option.uuid] === true}
								class:border-widget-color={optionTicked[option.uuid] !== true}
							>
								<label class="flex items-start gap-2 text-sm font-medium">
									<Checkbox
										bind:checked={optionTicked[option.uuid]}
										disabled={optionDisabled(option) || busy}
										spacing=""
										class="mt-0.5 shrink-0" />
									<span class="flex flex-wrap items-baseline gap-x-2 grow">
										<span>{option.title}</span>
										<!--
											Save or cost, from the **sign** of the option's own value,
											and coloured accordingly: green is the money colour for
											something earned, and an extra that reads green is an
											extra a customer ticks thinking it is free.
										-->
										{#if option.adjustmentType === 'percent' && option.percentAdjustment !== null}
											<span
												class="text-xs font-semibold {option.percentAdjustment < 0
													? 'text-green-700 dark:text-green-400'
													: 'text-default'}"
											>
												{option.percentAdjustment < 0 ? 'Save' : 'Add'}
												{Math.abs(option.percentAdjustment)}%
											</span>
										{:else if option.adjustmentType === 'fixed' && option.amount !== null}
											<!--
												Through `PriceDisplay` like every other money figure:
												the currency row carries its own `minorUnitDigits`,
												and dividing by 100 is right for USD and a hundred
												times wrong for JPY. The sign is spelled in words
												beside it rather than shown as a minus, because a
												bare "−€5.00" in a list of things to tick reads as
												an amount that is somehow negative.
											-->
											<span
												class="text-xs font-semibold inline-flex items-baseline gap-1 {option.amount < 0
													? 'text-green-700 dark:text-green-400'
													: 'text-default'}"
											>
												{option.amount < 0 ? 'Save' : 'Add'}
												<PriceDisplay
													amount={Math.abs(option.amount)}
													{currency}
													{currencyCode}
													showInterval={false}
													showTaxNote={false}
													showTrial={false}
													size="sm" />
											</span>
										{/if}
										{#if option.chargeOnce && isCharging(option)}
											<span class="text-xs text-default">one-off</span>
										{/if}
									</span>
								</label>
								<div class="richtext pl-6 mt-1 text-xs text-default">{@html option.bodyHTML}</div>
								{#if optionNote(option).length > 0}
									<p class="pl-6 mt-1 text-xs text-default">{optionNote(option)}</p>
								{/if}
							</div>
						{/each}
					</div>
				{/if}

				<div class="space-y-2">
					<Button
						color="primary"
						size="lg"
						class="w-full"
						disabled={!isOpen || busy || !canCommit || !termsDone}
						on:click={() => dispatcher('pay')}
					>
						{#if busy}<i class="fa-solid fa-spinner fa-spin mr-2" />{/if}{commitLabel}
					</Button>

					{#if !isChange}
						<p class="text-xs text-default text-center">
							<i class="fa-solid fa-lock mr-1.5" />You'll enter your card details on our payment
							provider's own page. They never reach this site.
						</p>
					{/if}
				</div>
			</div>
		{/if}
	</section>
</div>
